import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from "prop-types";
import { Table, Badge, Collapse, Button } from 'reactstrap';
import { translate } from "react-translate";
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';
import KafkaMessageModal from './KafkaMessageModal';

const TableKafka = (props) => {
    const { data, loading, t } = props;
    const [expandedRows, setExpandedRows] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState(false);

    const toggleRow = (index) => {
        const newExpandedRows = expandedRows.includes(index) 
            ? expandedRows.filter(i => i !== index) 
            : [...expandedRows, index];
        setExpandedRows(newExpandedRows);
    };

    const handleViewMessage = async (topic, partition, offset) => {
        setLoadingMessage(true);
        try {
            const token = localStorage.getItem('tsm-token');
            if (!token) {
                throw new Error(t('no_auth_token'));
            }

            const response = await fetch(`https://api.telepraxen.local/log/kafka/messages-kafka/${topic}/${partition}/${offset}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(t('http_error', {status: response.status}));
            }

            const result = await response.json();
            if (result.status === "success") {
                setSelectedMessage(result.data);
                setModalOpen(true);
            }
        } catch (error) {
            console.error(t('fetch_error'), error);
        } finally {
            setLoadingMessage(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'processed': { color: 'success', text: t('processed') },
            'no_changes': { color: 'info', text: t('no_changes') },
            'partial': { color: 'warning', text: t('partial') },
            'failed': { color: 'danger', text: t('failed') }
        };
        return (
            <Badge 
                color={statusMap[status]?.color || 'warning'} 
                className="text-capitalize"
                pill
            >
                {statusMap[status]?.text}
            </Badge>
        );
    };

    const getGlobalStatus = (consumers) => {
        if (consumers.every(c => c.status === 'processed')) return 'processed';
        if (consumers.some(c => c.status === 'failed')) return 'failed';
        return 'partial';
    };

    return (
        <div className="kafka-table-container">
            {loading ? (
                <LoadingLocal />
            ) : (
                <>
                    <Table hover responsive className="kafka-table">
                        <thead>
                            <tr>
                                <th width="25%">{t('event')}</th>
                                <th width="15%">{t('sender')}</th>
                                <th width="15%">{t('status')}</th>
                                <th width="15%">{t('consumers')}</th>
                                <th width="20%">{t('created')}</th>
                                <th width="10%"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.length > 0 ? (
                                data.map((message, index) => (
                                    <React.Fragment key={message._id}>
                                        <tr 
                                            onClick={() => toggleRow(index)}
                                            className={`kafka-row ${expandedRows.includes(index) ? 'expanded' : ''}`}
                                        >
                                            <td>
                                                <div className="event-title">
                                                    {message.event}
                                                </div>
                                                <div className="event-details">
                                                    <span>{t('id')}: {message.messageId}</span>
                                                    <span>{t('topic')}: {message.topic}</span>
                                                    <span>{t('partition')}: {message.partition}</span>
                                                    <span>{t('offset')}: {message.offset}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="sender-badge">
                                                    {message.sender}
                                                </span>
                                            </td>
                                            <td>
                                                {getStatusBadge(getGlobalStatus(message.consumers))}
                                            </td>
                                            <td>
                                                <div className="consumers-count">
                                                    {message.consumers.length} {t('consumers')}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="date-primary">
                                                    {moment(message.createdAt).format('DD MMM YYYY')}
                                                </div>
                                                <div className="date-secondary">
                                                    {moment(message.updatedAt).fromNow()}
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <Button 
                                                    color="primary" 
                                                    size="sm" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewMessage(message.topic, message.partition, message.offset);
                                                    }}
                                                    disabled={loadingMessage}
                                                >
                                                    {loadingMessage && selectedMessage?.offset === message.offset ? (
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        t('view_message')
                                                    )}
                                                </Button>
                                                {expandedRows.includes(index) ? '▲' : '▼'}
                                            </td>
                                        </tr>
                                        <tr className="details-row">
                                            <td colSpan={6} className="p-0">
                                                <Collapse isOpen={expandedRows.includes(index)}>
                                                    <div className="consumers-details">
                                                        <h5>{t('services_processing_details')}</h5>
                                                        <div className="consumers-list">
                                                            {message.consumers.map((consumer, i) => (
                                                                <div key={`${message._id}-${i}`} className="consumer-item">
                                                                    <div className="consumer-header">
                                                                        <span className="service-name">{consumer.service}</span>
                                                                        {getStatusBadge(consumer.status)}
                                                                    </div>
                                                                    <div className="consumer-description">
                                                                        {consumer.description}
                                                                    </div>
                                                                    <div className="consumer-meta">
                                                                        <span>
                                                                            {t('processed')}: {moment(consumer.processedAt).format('DD MMM HH:mm')}
                                                                        </span>
                                                                        {consumer.retries > 0 && (
                                                                            <span className="retries">
                                                                                {t('retries')}: {consumer.retries}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center py-4 text-muted">
                                        {t('no_messages_found')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <KafkaMessageModal 
                        isOpen={modalOpen} 
                        toggle={() => setModalOpen(!modalOpen)} 
                        messageData={selectedMessage} 
                    />
                </>
            )}
        </div>
    );
};

TableKafka.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.shape({
      messages: PropTypes.array,
      pagination: PropTypes.object
    }),
    PropTypes.array 
  ]),
  loading: PropTypes.bool
};

TableKafka.defaultProps = {
    data: {
        messages: [],
        pagination: {}
    },
    loading: false
};

export default translate("kafka")(TableKafka);